import React from 'react';
import { Meteor } from 'meteor/meteor';
import PropTypes from 'prop-types';
import { withTracker } from 'meteor/react-meteor-data';
import { find, isEmpty, toNumber, cloneDeep, isNil, includes, without } from 'lodash';
import { Box, Stack } from '@mui/material';
import turf from '@turf/turf';
import Loading from '../Loading';

import Layers from '../../../api/Layers/Layers';
import * as layerOps from '../../../api/Layers/layer-operations';
import Styles from '../../../api/Styles/Styles';
import { MAP_TYPES } from '../../../api/Maps/Maps';
import { featurePoint2LatLng, featureGetZoom } from '../../../modules/geojson-utilities';
import * as videoOps from '../../../api/Videos/video-operations';

import LeafletMap from '../LeafletMap';
import LayerGallerySearchContainer from '../../containers/LayerGallerySearchContainer';
import LayersSelectedList from '../../atoms/LayersSelectedList';

let addOnlineBaseMap;
let Geocoding;

let drawVideoTrack;
let drawCustomButtons;
let drawMapLayers;

const VideoEditorBaseMaps = ({
  deployEnv,
  loading,
  layers,
  styles,
  mapLocation,
  updatedVideo,
  updateVideoProperty,
  userControlSetting
}) => {
  if (loading) return <Loading />;

  const videoEditorBaseMap = React.createRef();

  let baseMap = null;

  const [pageToRestore, setPageToRestore] = React.useState(1);
  const [isLeafletMapLoaded, setIsLeafletMapLoaded] = React.useState(false);
  const [isVideoEditorMapLoaded, setIsVideoEditorMapLoaded] = React.useState(false);
  const [clientModulesReady, setClientModulesReady] = React.useState(false);

  const [baseMapSelected, setBaseMapSelected] = React.useState([]);
  const [baseMapsTransparency, setBaseMapsTransparency] = React.useState([]);
  const [baseMapsZoom, setBaseMapsZoom] = React.useState([]);

  const [selectedLayers, setSelectedLayers] = React.useState([]);
  const [layersTransparency, setLayersTransparency] = React.useState([]);
  const [layersZoom, setLayersZoom] = React.useState([]);

  const initializeMap = async () => {
    const { baseMaps, layersMaps } = videoOps.getGeomotionMap(updatedVideo);
    const base = !isEmpty(baseMaps) ? baseMaps : [];
    const layer = !isEmpty(layersMaps) ? layersMaps : [];

    const location = turf.point(videoOps.getLatLng0(updatedVideo), { zoom: 16 });
    baseMap = videoEditorBaseMap.current.leafletMap;
    baseMap.setView(featurePoint2LatLng(mapLocation), featureGetZoom(mapLocation));
    const searchControl = Geocoding.geosearch({ useMapBounds: false });
    baseMap.addControl(searchControl);
    baseMap.searchControl = searchControl;

    if (!isEmpty(layersMaps)) {
      const layerId = layersMaps[0]._id;
      setSelectedLayers([layerId]);
    }

    if (!isEmpty(baseMaps)) {
      const baseMapId = baseMaps[0]._id;
      setBaseMapSelected([baseMapId]);
      drawMapLayers({
        layers: layer,
        baseMaps: base,
        popupProp: false,
        leafletMap: baseMap,
        layersToDisplay: null,
        baseMapsToDisplay: null,
        isUpdate: false,
        baseMapsTransparency: [],
        layersTransparency: [],
        baseMapsZoom,
        layersZoom: []
      });
    } else addOnlineBaseMap(baseMap);

    drawVideoTrack({
      leafletMap: baseMap,
      track: videoOps.getTrack(updatedVideo),
      isSynchronizer: false
    });

    drawCustomButtons({
      map: {
        selectedButtons: {
          Attribution: true,
          Home: true,
          QRCode: false,
          Scale: true,
          Search: false,
          Title: false,
          Zoom: true
        },
        mapLocation: location,
        name: videoOps.getName(updatedVideo)
      },
      type: 'video',
      leafletMap: baseMap
    });
    setIsVideoEditorMapLoaded(true);
  };

  const updateMapState = newState => {
    setIsLeafletMapLoaded(newState);
  };

  const savePage = pageNumber => {
    setPageToRestore(pageNumber);
  };

  const selectBaseMap = layerId => {
    const { layersMaps } = videoOps.getGeomotionMap(updatedVideo);
    const newSelectedBaseMaps = includes(baseMapSelected, layerId)
      ? without(baseMapSelected, layerId)
      : [...baseMapSelected, layerId];
    const layer = [find(layers, ['_id', layerId])];
    updateVideoProperty('geomotionMap', {
      baseMaps: !isEmpty(newSelectedBaseMaps) ? layer : [],
      layersMaps: !isEmpty(layersMaps) ? layersMaps : []
    });
    setBaseMapSelected(newSelectedBaseMaps);
  };

  const selectLayer = layerId => {
    const { baseMaps } = videoOps.getGeomotionMap(updatedVideo);
    const newSelectedLayers = includes(selectedLayers, layerId)
      ? without(selectedLayers, layerId)
      : [...selectedLayers, layerId];
    const layer = [find(layers, ['_id', layerId])];
    updateVideoProperty('geomotionMap', {
      baseMaps: !isEmpty(baseMaps) ? baseMaps : [],
      layersMaps: !isEmpty(newSelectedLayers) ? layer : []
    });
    setSelectedLayers(newSelectedLayers);
  };

  const changeBaseMapsZoom = (value, layer, isMaxZoom = true) => {
    const newBaseMapsZoom = cloneDeep(baseMapsZoom);

    const zoom = isMaxZoom ? 'maxZoom' : 'minZoom';
    const otherZoom = isMaxZoom ? 'minZoom' : 'maxZoom';
    const otherZoomValue = isMaxZoom ? layerOps.getMinZoom(layer) : layerOps.getMaxZoom(layer);

    const baseMapZoom = find(newBaseMapsZoom, ['id', layerOps.getId(layer)]);
    if (!isNil(baseMapZoom)) {
      baseMapZoom[zoom] = toNumber(value);
    } else {
      newBaseMapsZoom.push({
        id: layerOps.getId(layer),
        [zoom]: toNumber(value),
        [otherZoom]: otherZoomValue
      });
    }
    setBaseMapsZoom(newBaseMapsZoom);
  };

  const changeLayersZoom = (value, layer, isMaxZoom = true) => {
    const newLayersZoom = cloneDeep(layersZoom);

    const zoom = isMaxZoom ? 'maxZoom' : 'minZoom';
    const otherZoom = isMaxZoom ? 'minZoom' : 'maxZoom';
    const otherZoomValue = isMaxZoom ? layerOps.getMinZoom(layer) : layerOps.getMaxZoom(layer);

    const layerZoom = find(newLayersZoom, ['id', layerOps.getId(layer)]);
    if (!isNil(layerZoom)) {
      layerZoom[zoom] = toNumber(value);
    } else {
      newLayersZoom.push({
        id: layerOps.getId(layer),
        [zoom]: toNumber(value),
        [otherZoom]: otherZoomValue
      });
    }

    setLayersZoom(newLayersZoom);
  };

  const changeBaseMapsTransparency = (event, layer) => {
    const value = toNumber(event.target.value);
    const newBaseMapsTransparency = cloneDeep(baseMapsTransparency);

    const baseMapTransparency = find(newBaseMapsTransparency, ['id', layerOps.getId(layer)]);
    if (!isNil(baseMapTransparency)) baseMapTransparency.value = value;
    else newBaseMapsTransparency.push({ id: layerOps.getId(layer), value });
    setBaseMapsTransparency(newBaseMapsTransparency);
  };

  const changeLayersTransparency = (event, layer) => {
    const value = toNumber(event.target.value);
    const newLayersTransparency = cloneDeep(layersTransparency);

    const layerTransparency = find(newLayersTransparency, ['id', layerOps.getId(layer)]);
    if (!isNil(layerTransparency)) layerTransparency.value = value;
    else newLayersTransparency.push({ id: layerOps.getId(layer), value });

    setLayersTransparency(newLayersTransparency);
  };

  const leafletDrawLayer = async () => {
    const { baseMaps, layersMaps } = videoOps.getGeomotionMap(updatedVideo);
    const base = !isEmpty(baseMaps) ? baseMaps : [];
    const layer = !isEmpty(layersMaps) ? layersMaps : [];
    await drawMapLayers({
      layers: layer,
      baseMaps: base,
      popupProp: false,
      leafletMap: videoEditorBaseMap.current.leafletMap,
      layersToDisplay: layer,
      baseMapsToDisplay: base,
      isUpdate: true,
      baseMapsTransparency: [],
      layersTransparency: [],
      baseMapsZoom,
      layersZoom
    });
  };

  const loadClientModules = async () => {
    if (Meteor.isClient) {
      Geocoding = await import('esri-leaflet-geocoder');
      ({ addOnlineBaseMap, drawVideoTrack, drawCustomButtons, drawMapLayers } = await import(
        '../../../modules/leaflet-operations'
      ));
      setClientModulesReady(true);
    }
  };

  React.useEffect(() => {
    if (!clientModulesReady) loadClientModules();
  }, []);

  React.useEffect(() => {
    if (
      !isEmpty(videoEditorBaseMap) &&
      isLeafletMapLoaded &&
      clientModulesReady &&
      !isVideoEditorMapLoaded
    ) {
      initializeMap();
    }
  }, [videoEditorBaseMap]);

  React.useEffect(() => {
    const { baseMaps } = videoOps.getGeomotionMap(updatedVideo);
    if (!isEmpty(baseMaps) && clientModulesReady) {
      leafletDrawLayer();
    }
  }, [updatedVideo]);

  return (
    <>
      <Box width="100%" height="100%">
        <Stack direction="row">
          <Box width="50%" height="95%" paddingRight="2%">
            <LayerGallerySearchContainer
              selectBaseMap={selectBaseMap}
              selectLayer={selectLayer}
              selectedLayers={selectedLayers}
              selectedBaseMaps={baseMapSelected}
              deployEnv={deployEnv}
              pageToRestore={pageToRestore}
              savePage={savePage}
              mapType={MAP_TYPES.LEAFLET}
              userControlSetting={userControlSetting}
            />
            <Stack direction="row" justifycontent="space-between">
              <LayersSelectedList
                selectedLayers={baseMapSelected}
                selectLayer={selectBaseMap}
                changeTransparency={changeBaseMapsTransparency}
                layersTransparency={baseMapsTransparency}
                changeZoom={changeBaseMapsZoom}
                layersZoom={baseMapsZoom}
                layers={layers}
                styles={styles}
                listTitle="Base Maps"
                emptyText="Add a base map"
              />
              <LayersSelectedList
                selectedLayers={selectedLayers}
                selectLayer={selectLayer}
                changeTransparency={changeLayersTransparency}
                layersTransparency={layersTransparency}
                changeZoom={changeLayersZoom}
                layersZoom={layersZoom}
                layers={layers}
                styles={styles}
                listTitle="Layers"
                emptyText="Add a layer"
              />
            </Stack>
          </Box>
          <Box width="50%" height="100%">
            <LeafletMap
              mapId="baseMap"
              ref={videoEditorBaseMap}
              deployEnv={deployEnv}
              updateMapState={updateMapState}
              headerType="8"
            />
          </Box>
        </Stack>
      </Box>
    </>
  );
};

VideoEditorBaseMaps.defaultProps = {
  deployEnv: 'gmv',
  layers: [],
  styles: []
};

VideoEditorBaseMaps.propTypes = {
  deployEnv: PropTypes.string,
  layers: PropTypes.array,
  styles: PropTypes.array,
  mapLocation: PropTypes.object.isRequired,
  loading: PropTypes.bool.isRequired,
  updatedVideo: PropTypes.object.isRequired,
  updateVideoProperty: PropTypes.func.isRequired,
  userControlSetting: PropTypes.object.isRequired
};

export default withTracker(() => {
  const layersSub = Meteor.subscribe('layers');
  const stylesSub = Meteor.subscribe('styles');
  return {
    loading: !layersSub.ready() || !stylesSub.ready(),
    layers: Layers.find({ deleted: { $eq: 'no' } }).fetch(),
    styles: Styles.find({ deleted: { $eq: 'no' } }).fetch()
  };
})(VideoEditorBaseMaps);
